import React from 'react'
import { connect, useSelector } from 'react-redux'
import { bindActionCreators, Dispatch } from 'redux'
import clsx from 'clsx'
import InputAdornment from '@mui/material/InputAdornment'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import Typography from '@mui/material/Typography'
import { RootActionType, RootState } from 'duck'
import { actions, selectors, FormDataType, FORM_OPTIONS } from '../../duck'
import { ReduxProps } from 'utils/Types'
import Tooltip from 'components/Tooltip'
import GridLabelTwo from 'components/GridLabel/GridLabelTwo'
import SelectBase from 'components/Select/SelectBase'
import Link from 'components/Link'
import { useBreakpoint } from 'utils/Hooks'
import TextFieldItem from './TextFieldItem'
import EthereumIcon from 'components/Icon/EthereumIcon'
import PolygonIcon from 'components/Icon/PolygonIcon'
import { TextTransform } from 'utils/TextUtils'

const mapDispatchToProps = (dispatch: Dispatch<RootActionType>) =>
  bindActionCreators(
    {
      updateFormData: actions.updateFormData
    },
    dispatch
  )

type PaymentDetailsProps = ReduxProps<typeof mapDispatchToProps> & {
  setRef?: React.Ref<HTMLDivElement>
}

const PaymentDetails: React.FC<PaymentDetailsProps> = props => {
  const { setRef, updateFormData } = props

  const network = useSelector((state: RootState) => selectors.formData(state).network)
  const price_method = useSelector((state: RootState) => selectors.formData(state).price_method)
  const license = useSelector((state: RootState) => selectors.formData(state).license)
  const disabled = useSelector((state: RootState) => selectors.isResubmitForm(state))

  const isPriceFixed = price_method === 'fixed'

  const { isMediumscreenAbove } = useBreakpoint()

  return (
    <div ref={setRef} className={isMediumscreenAbove ? 'mb-11' : 'mb-8'}>
      <Typography variant="h6" className="mb-7">
        Pricing
      </Typography>

      <GridLabelTwo
        className="mb-6"
        label={
          <span className="flex items-center">
            <Typography variant="overline">Choose Blockchain Network</Typography>
            <Tooltip
              className="ml-2"
              title={
                <Typography variant="caption">
                  <span className="font-bold text-white-hi-on-dark">Ethereum</span> has more whales,
                  but but <span className="font-bold text-white-hi-on-dark">Polygon</span> doesn’t
                  have gas fees making it more accessible. Polygon NFTs can be transferred to the
                  Ethereum blockchain in the future.
                </Typography>
              }
            >
              <HelpOutlineIcon className="text-base" />
            </Tooltip>
          </span>
        }
        labelColor="light"
        content={
          <SelectBase<FormDataType['network']>
            onChange={value => {
              updateFormData({ network: value })
            }}
            disabled={disabled}
            items={FORM_OPTIONS.networks}
            value={network}
            size="small"
            inputAlign="right"
            menuTextTransform="capitalize"
            autoWidth
            borderless
          />
        }
      />

      <GridLabelTwo
        className="mb-6"
        label={
          <span className="flex items-center">
            <Typography variant="overline">Pricing Model</Typography>

            <Tooltip
              className="ml-2"
              title={
                <div>
                  <div className="mb-4">
                    <Typography
                      variant="caption"
                      component="div"
                      className="text-white-hi-on-dark"
                      fontWeight="bold"
                    >
                      Fixed Price
                    </Typography>

                    <Typography variant="caption" component="div">
                      One price for all NFTs
                    </Typography>
                  </div>

                  <div className="mb-4">
                    <Typography
                      variant="caption"
                      component="div"
                      className="text-white-hi-on-dark"
                      fontWeight="bold"
                    >
                      Increasing Price
                    </Typography>

                    <Typography variant="caption" component="div">
                      Price goes up as NFTs sell
                    </Typography>
                  </div>

                  <div>
                    <Typography
                      variant="caption"
                      component="div"
                      className="text-white-hi-on-dark"
                      fontWeight="bold"
                    >
                      Decreasing Price
                    </Typography>

                    <Typography variant="caption" component="div">
                      Price goes down as NFTs sell
                    </Typography>
                  </div>
                </div>
              }
            >
              <HelpOutlineIcon className="text-base" />
            </Tooltip>
          </span>
        }
        labelColor="light"
        content={
          <SelectBase<FormDataType['price_method']>
            onChange={value => {
              updateFormData({ price_method: value })
            }}
            disabled={disabled}
            label="Pricing Model"
            value={price_method}
            items={FORM_OPTIONS.price_method}
            size="small"
            inputAlign="right"
            menuTextTransform="capitalize"
            autoWidth
            borderless
          />
        }
      />

      <div className="mb-3">
        <TextFieldItem
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {network === 'POLYGON' ? <PolygonIcon /> : <EthereumIcon />}
              </InputAdornment>
            )
          }}
          tooltipData={{
            className: 'text-white-med-on-dark',
            classes: {
              tooltip: 'max-w-none'
            },
            title: (
              <Typography variant="caption">
                <span className="font-bold text-white-hi-on-dark">Some things to consider:</span>
                <br />
                - How many NFTs are you issuing? <br />
                - Have you sold NFTs before? <br />- What blockchain are you selling on?
              </Typography>
            )
          }}
          type={'number'}
          formDataType={'start_price'}
          label={
            isPriceFixed
              ? `Price (${TextTransform.textCase(network, 'titlecase')}) per NFT`
              : 'Start Price'
          }
        />

        <TextFieldItem
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {network === 'POLYGON' ? <PolygonIcon /> : <EthereumIcon />}
              </InputAdornment>
            )
          }}
          className={clsx('mt-6', isPriceFixed ? 'hidden' : null)}
          type={'number'}
          formDataType={'end_price'}
          label="End Price"
        />
      </div>

      <GridLabelTwo
        className="mb-3"
        label={
          <span className="flex items-center">
            <Typography variant="overline">Choose Licensing Terms</Typography>
            <Tooltip
              className="ml-2"
              title={
                <Typography variant="caption">
                  <span className="font-bold text-white-hi-on-dark">For more information:</span>
                  <br />
                  NFT License 2.0 -{' '}
                  <Link href="http://nftlicense.org" target="_blank">
                    nftlicense.org
                  </Link>
                  <br />
                  CC BY-NC 4.0 -{' '}
                  <Link href="http://nftlicense.org" target="_blank">
                    creativecommons.org
                  </Link>
                </Typography>
              }
            >
              <HelpOutlineIcon className="text-base" />
            </Tooltip>
          </span>
        }
        labelColor="light"
        content={
          <SelectBase<string>
            onChange={value => {
              updateFormData({ license: value })
            }}
            disabled={disabled}
            value={license}
            items={FORM_OPTIONS.licenses}
            size="small"
            inputAlign="right"
            menuTextTransform="capitalize"
            autoWidth
            borderless
          />
        }
      />
    </div>
  )
}

export default connect(null, mapDispatchToProps)(PaymentDetails)
